import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Checkbox, Table} from "@wfp/ui";
import {Crate, KVPair, ListPage, OrderItem, OrderItemState, Role, Sortable} from "../../models/types";
import {useLoggedUser} from "../../hooks/user";
import {TablePagination} from "../commons/table-pagination/table-pagination";
import {StatusBadge} from "../commons/status-badge";

interface Props {
    hidePagination?: boolean;
    page: ListPage<OrderItem>;
    loadPage: (page: number, pageSize?: number, filtering?: KVPair[], sorting?: string[]) => void;
    onSelectOrderItems?: (orderItemsId: number[]) => void;
    onClickOrderItem?: (o: OrderItem) => void;
    sortInfo?: Sortable[];
    sort?: (field: string) => void;
}

const staticHeaders = ["school_name"];
const staticSuffixHeaders = ["total_quantity_items_am", "total_quantity_items_pm", "total_quantity_items", "quantity_snacks_am", "quantity_snacks_pm", "snacks", "state"];

const staticSortInfo: Sortable[] = [
    {field: 'school_name', code: 'school_name', enabled: true, mode: 0},
];

const staticSuffixSortInfo: Sortable[] = [
    {field: 'total_quantity_items_am', code: 'totalQuantityItemsAM', enabled: false, mode: 0},
    {field: 'total_quantity_items_pm', code: 'totalQuantityItemsPM', enabled: false, mode: 0},
    {field: 'total_quantity_items', code: 'totalQuantityItems', enabled: false, mode: 0},
    {field: 'quantity_am', code: 'quantitySnacksAM', enabled: true, mode: 0},
    {field: 'quantity_pm', code: 'quantitySnacksPM', enabled: true, mode: 0},
    {field: 'snacks', code: 'items', enabled: false, mode: 0},
    {field: 'state', code: 'state', enabled: true, mode: 0},
];

export function OrderItemSnacksTable(props: Props) {
    const {t} = useTranslation();
    const user = useLoggedUser({});
    const [allRowsSelected, setAllRowsSelected] = useState<boolean>(false);
    const [rowsSelected, setRowsSelected] = useState<boolean[]>([]);

    // Get the number of unique snacks from the first order item
    const numSnacks = props.page.results[0]?.contentNames?.length || 0;

    // Build dynamic headers based on actual number of snacks
    const dynamicItemHeaders: string[] = [];
    const dynamicItemSortInfo: Sortable[] = [];
    for (let i = 1; i <= numSnacks; i++) {
        dynamicItemHeaders.push(`item${i}`, `quantity_item${i}_AM`, `quantity_item${i}_PM`);
        dynamicItemSortInfo.push(
            {field: '', code: `item${i}`, enabled: false, mode: 0},
            {field: `quantity_item${i}_am`, code: `quantityItem${i}AM`, enabled: false, mode: 0},
            {field: `quantity_item${i}_pm`, code: `quantityItem${i}PM`, enabled: false, mode: 0}
        );
    }

    const headers = [...staticHeaders, ...dynamicItemHeaders, ...staticSuffixHeaders];
    const sortInfo = [...staticSortInfo, ...dynamicItemSortInfo, ...staticSuffixSortInfo];

    const [sortables, setSortables] = useState<Sortable[]>(sortInfo);

    useEffect(() => {
        if (!!props.page) {
            const allRowsSelected = props.page.results.map(() => false);
            setRowsSelected(allRowsSelected);
        }
    }, [props.page]);

    useEffect(() => {
        setSortables(sortInfo);
    }, [numSnacks]);

    useEffect(() => {
        if (!!props.sortInfo) {
            const sorts = headers.map((header) => {
                const sort = props.sortInfo?.find((sort) => sort.code === header);
                return !!sort ? sort : {code: header, field: header, mode: 0, enabled: false}
            });
            setSortables(sorts);
        }
    }, [props.sortInfo, numSnacks]);

    function checkOrderItem(idx: number) {
        if (idx === -1) {
            setAllRowsSelected(!allRowsSelected);
            rowsSelected.forEach((_, index) => rowsSelected[index] = !allRowsSelected);
        } else {
            rowsSelected[idx] = !rowsSelected[idx];
            const selected = rowsSelected.filter(f => f);
            if (selected.length === 0) {
                setAllRowsSelected(false);
                idx = -1;
            }
            if (selected.length === rowsSelected.length) {
                setAllRowsSelected(true);
                idx = -1;
            }
        }
        setRowsSelected([...rowsSelected]);
        if (!!props.onSelectOrderItems) {
            if (idx === -1) {
                props.onSelectOrderItems(allRowsSelected ? [] : [-1]);
            } else {
                props.onSelectOrderItems(props.page.results.filter((_, idx) => rowsSelected[idx]).map((oi) => oi.id));
            }
        }
    }

    function handleSorting(idx: number) {
        if (!!sortables && !!props.sort) {
            const sortable = sortables[idx];
            if (!sortable.enabled) {
                return;
            }
            sortable.mode = (sortable.mode + 1) % 3;
            setSortables([...sortables]);
            const value = sortable.mode === 1 ? `+${sortable.field}` : sortable.mode === 2 ? `-${sortable.field}` : sortable.field;
            props.sort(value);
        }
    }

    function handleClickOrderItem(item: OrderItem): void {
        if (!!props.onClickOrderItem && (item.state === OrderItemState.CONFIRMED || OrderItemState.PLANNED) && user?.role === Role.PartnerManager) {
            props.onClickOrderItem(item);
        }
    }

    function formatContentNameQuantityRow(contentNames: string[] | null, crates: Crate[] | null): string[] {
        // Returns the quantity of each crate in the orderItem: Item1Name,AM1,PM1,Item2Name,AM2,PM2,...,TotalAM,TotalPM,Total
        if (!crates || !contentNames || contentNames.length === 0) {
            // Return empty array with 3 values for totals
            return ['0/0', '0/0', '0/0'];
        }

        const rowContentNameQuantity: string[] = [];
        let totalAcceptedAM = 0;
        let totalQuantityAM = 0;
        let totalAcceptedPM = 0;
        let totalQuantityPM = 0;

        // Sort contentNames to ensure consistent ordering (matches backend sorting)
        const sortedContentNames = [...contentNames].sort();

        // For each snack, add: snack name, AM quantity, PM quantity
        sortedContentNames.forEach((contentName) => {
            // Push the actual snack name (not a quantity string)
            rowContentNameQuantity.push(contentName || '');

            let acceptedAM = 0;
            let quantityAM = 0;
            let acceptedPM = 0;
            let quantityPM = 0;

            // Find matching crates for this content name
            crates.forEach((c) => {
                if (c.contentName === contentName && c.crateType === 0) { // AM
                    acceptedAM += c.acceptedQuantity || 0;
                    quantityAM += c.quantity || 0;
                } else if (c.contentName === contentName && c.crateType === 1) { // PM
                    acceptedPM += c.acceptedQuantity || 0;
                    quantityPM += c.quantity || 0;
                }
            });

            totalAcceptedAM += acceptedAM;
            totalQuantityAM += quantityAM;
            totalAcceptedPM += acceptedPM;
            totalQuantityPM += quantityPM;

            rowContentNameQuantity.push(`${acceptedAM}/${quantityAM}`);
            rowContentNameQuantity.push(`${acceptedPM}/${quantityPM}`);
        });

        // Add totals
        rowContentNameQuantity.push(`${totalAcceptedAM}/${totalQuantityAM}`);
        rowContentNameQuantity.push(`${totalAcceptedPM}/${totalQuantityPM}`);
        rowContentNameQuantity.push(`${totalAcceptedAM + totalAcceptedPM}/${totalQuantityAM + totalQuantityPM}`);

        return rowContentNameQuantity;
    }

    return (
        <div className="overflow-x-auto">
            <Table>
                <thead>
                <tr className="item-row-head">
                    {(user?.role === Role.SupplierManager || user?.role === Role.WFPManager) && (
                        <th>
                            <Checkbox id='all' labelText={''} name='row-select' value={-1}
                                      checked={allRowsSelected || false}
                                      onClick={() => checkOrderItem(-1)}></Checkbox>
                        </th>
                    )}
                    {headers.map((header, idx) => (
                        <th key={idx} onClick={() => handleSorting(idx)}
                            className={props.sort && sortables?.at(idx)?.enabled ? "cursor-pointer" : ""}>
                            {t(header)}
                            {!!sortables?.at(idx) && (
                                <span className="ml-0.5">
                                {sortables[idx].mode === 2 ? '\u25bc' : sortables[idx].mode === 0 ? '\u2000' : '\u25b2'}
                            </span>
                            )}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {!!props.page && props.page.results.map((item, idx) => (
                    <tr className={`item-row ${(item.state === OrderItemState.CONFIRMED || OrderItemState.PLANNED) && user?.role === Role.PartnerManager ? 'hover:bg-slate-100 cursor-pointer' : ''}`}
                        onClick={() => handleClickOrderItem(item)} key={item.id}>
                        {(user?.role === Role.SupplierManager || user?.role === Role.WFPManager) && (
                            <td>
                                <Checkbox id={`${item.id}`} labelText={''} name="row-select" value={item.id}
                                          checked={rowsSelected[idx] || false}
                                          onClick={() => checkOrderItem(idx)}></Checkbox>
                            </td>
                        )}
                        <td className="item-col text-start">
                            {item.schoolName}
                        </td>
                        {formatContentNameQuantityRow(item.contentNames || ['', ''], item.crates).map((quantity, idx) => (
                            <td key={idx} className="item-col text-start">
                                {quantity}
                            </td>
                        ))}
                        <td className="item-col text-start">
                            {item.quantityAm}
                        </td>
                        <td className="item-col text-start">
                            {item.quantityPm}
                        </td>
                        <td className="item-col text-start">
                            {item.quantity}
                        </td>
                        <td className="item-col text-start">
                            <StatusBadge status={item.pendingDiscardRequest ? "DISCARD_REQUESTED" : item.state} />
                        </td>
                    </tr>
                ))}
                </tbody>
            </Table>
            {!props.hidePagination && (
                <TablePagination page={props.page} loadPage={props.loadPage}/>
            )}
        </div>
    )
}
